import { unsafeCoerce } from 'fp-ts/lib/function';

/** Maps an object to a Partial version of that object by allowing a callback
 * to transform the keys and values.
 *
 * The callback takes a single `Entry` and returns an array of `Entry`.
 */
export const flatMapObject = <Input extends object, Output = Input>(
  obj: Input,
  flatMapper: (entry: Entry<Input>) => readonly Entry<Output>[],
): Partial<Output> => safeFromEntries(safeToEntries(obj).flatMap(flatMapper));

/** An object like `{key: ..., value: ...}` which represents a single property in a type `T`. */
export type Entry<T> = Required<
  {
    [Key in keyof T]: {
      key: Key;
      value: T[Key];
    };
  }
>[keyof T];

/** Transform an object to an array of `Entry`. */
export const safeToEntries = <T extends object>(obj: T): Entry<T>[] =>
  unsafeCoerce(Object.entries(obj).map(([key, value]: [string, unknown]) => ({ key, value })));

/** Transform an array of `Entry<T>` back into an object. Because we can't
 * guarantee all the keys are present, the result is a `Partial<T>`.
 */
export const safeFromEntries = <T,>(entries: Entry<T>[]): Partial<T> =>
  unsafeCoerce(Object.fromEntries(entries.map(({ key, value }) => [key, value])));
